import { useMemo } from "react";
import PropTypes from "prop-types";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";

import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

export const localToday = (now = new Date()) => {
  const pad = (part) => String(part).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const isCalendarDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

// A multi-selection permits a date from its earliest invoice, then reports any
// later invoice that conflicts. The server remains authoritative for hidden dates.
export const paymentDateBounds = (invoices = [], today = localToday()) => {
  const dates = invoices.map((invoice) => invoice?.invoice_date).filter(isCalendarDate).sort();
  return { minDate: dates[0] || undefined, maxDate: today };
};

export const validatePaymentDate = (value, invoices = [], today = localToday()) => {
  if (!value) return "Please select a payment date in YYYY-MM-DD format.";
  if (!isCalendarDate(value)) return "Enter a valid payment date in YYYY-MM-DD format.";
  if (value > today) {
    return `Payment date ${value} cannot be in the future. Choose ${today} or an earlier date.`;
  }
  const conflict = invoices.find((invoice) => (
    isCalendarDate(invoice?.invoice_date) && value < invoice.invoice_date
  ));
  if (conflict) {
    const invoiceLabel = conflict.invoice_number || (conflict.id ? `#${conflict.id}` : "the selected invoice");
    return `Payment date ${value} cannot be before the invoice date for invoice ${invoiceLabel} (${conflict.invoice_date}). Choose ${conflict.invoice_date} or a later date.`;
  }
  return "";
};

function InvoicePaymentDate({ value, onChange, invoices = [], error = "", disabled = false, id = "invoice-payment-date" }) {
  const today = localToday();
  const { minDate, maxDate } = paymentDateBounds(invoices, today);
  const options = useMemo(() => ({
    dateFormat: "Y-m-d",
    minDate,
    maxDate,
    allowInput: false,
    disableMobile: true,
    clickOpens: !disabled,
    onDayCreate: (_dates, _dateString, _instance, dayElement) => {
      const date = localToday(dayElement.dateObj);
      if ((minDate && date < minDate) || date > maxDate) {
        // Keep the calendar grid aligned without displaying dates that cannot
        // be used for this payment. minDate/maxDate still enforce selection.
        dayElement.style.visibility = "hidden";
        dayElement.setAttribute("aria-hidden", "true");
      }
    },
  }), [minDate, maxDate, disabled]);

  return (
    <SoftBox mt={2}>
      <SoftTypography component="label" htmlFor={id} variant="caption" display="block" mb={0.5}>
        Payment Date (YYYY-MM-DD)
      </SoftTypography>
      <Flatpickr
        value={value}
        options={options}
        onChange={(_dates, dateString) => onChange(dateString)}
        render={({ defaultValue }, ref) => (
          <SoftInput
            id={id}
            inputRef={ref}
            defaultValue={defaultValue}
            disabled={disabled}
            error={Boolean(error)}
            placeholder="YYYY-MM-DD"
            inputProps={{ readOnly: true, disabled, "aria-describedby": `${id}-help`, "aria-invalid": Boolean(error) }}
          />
        )}
      />
      <SoftTypography id={`${id}-help`} variant="caption" display="block" mt={0.5} color={error ? "error" : "text"} role={error ? "alert" : undefined}>
        {error || (minDate
          ? `Select a date from ${minDate} to ${maxDate}. Future dates are not allowed.`
          : `Select a date on or before ${maxDate}. Future dates are not allowed.`)}
      </SoftTypography>
    </SoftBox>
  );
}

InvoicePaymentDate.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  invoices: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

export default InvoicePaymentDate;
